import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles, PenTool, Lightbulb, Target, Heart, Brain, Zap } from 'lucide-react';

const AnalysisLoader = ({ stage = 'analyzing' }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('opacity-100');

  // Compelling and unexpected writing tips
  const writingTips = [
    {
      icon: <Sparkles className="w-5 h-5" />,
      tip: "The best essays often start in the middle of the action",
      detail: "Skip the setup and drop readers into your pivotal moment"
    },
    {
      icon: <Target className="w-5 h-5" />,
      tip: "Specificity is the soul of narrative",
      detail: "One perfect detail beats ten generic descriptions"
    },
    {
      icon: <Heart className="w-5 h-5" />,
      tip: "Your essay should pass the 'swap test'",
      detail: "No other student could submit it as their own"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      tip: "Admissions officers read 50+ essays a day",
      detail: "Make yours the one they remember at dinner"
    },
    {
      icon: <PenTool className="w-5 h-5" />,
      tip: "The magic happens in revision",
      detail: "Hemingway rewrote the ending of Farewell to Arms 39 times"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      tip: "Show your thinking, not just your achievements",
      detail: "How you process experiences matters more than what happened"
    },
    {
      icon: <Lightbulb className="w-5 h-5" />,
      tip: "The smallest moments often make the biggest impact",
      detail: "A gesture, a glance, a single word can reveal everything"
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      tip: "Vulnerability is strength in college essays",
      detail: "Your struggles and doubts make you human and relatable"
    },
    {
      icon: <Target className="w-5 h-5" />,
      tip: "End with forward momentum",
      detail: "Show how this experience propels you toward your future"
    },
    {
      icon: <Heart className="w-5 h-5" />,
      tip: "Use all five senses in your descriptions",
      detail: "What did it smell like? Sound like? Feel like?"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      tip: "Your first draft is just material to work with",
      detail: "Real writing is rewriting - embrace the messy middle"
    },
    {
      icon: <PenTool className="w-5 h-5" />,
      tip: "Active voice creates immediacy",
      detail: "'I discovered' hits harder than 'It was discovered by me'"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      tip: "Surprise yourself in your own essay",
      detail: "If you're bored writing it, they'll be bored reading it"
    },
    {
      icon: <Lightbulb className="w-5 h-5" />,
      tip: "Context matters more than accomplishments",
      detail: "What you did with limited resources says everything"
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      tip: "Your voice should sound like you at your best",
      detail: "Authentic doesn't mean unpolished - it means genuinely you"
    }
  ];

  // Rotate through tips every 3.5 seconds with fade animation
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeClass('opacity-0');
      setTimeout(() => {
        setCurrentTipIndex((prev) => (prev + 1) % writingTips.length);
        setFadeClass('opacity-100');
      }, 300);
    }, 3500);

    return () => clearInterval(interval);
  }, [writingTips.length]);

  const currentTip = writingTips[currentTipIndex];

  // Analysis stages for progress indication
  const stages = {
    analyzing: "Reading your essay carefully...",
    understanding: "Understanding your narrative arc...",
    identifying: "Identifying strengths and opportunities...",
    crafting: "Crafting personalized feedback...",
    finalizing: "Adding final touches...",
    processing: "Preparing your personalized feedback..."
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      {/* Main loader */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-400 to-indigo-400 rounded-full blur-xl opacity-20 animate-pulse" />
        <Loader2 className="w-12 h-12 text-violet-600 animate-spin relative z-10" />
      </div>

      {/* Stage indicator */}
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-slate-800">
          Analyzing Your Essay
        </h3>
        <p className="text-sm text-slate-600 animate-pulse">
          {stages[stage] || stages.analyzing}
        </p>
      </div>

      {/* Writing tip carousel */}
      <div className={`transition-opacity duration-300 ${fadeClass} max-w-md`}>
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-violet-100">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 text-violet-600 mt-0.5">
              {currentTip.icon}
            </div>
            <div className="flex-1 space-y-2">
              <p className="font-medium text-slate-900 text-sm leading-tight">
                {currentTip.tip}
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">
                {currentTip.detail}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex space-x-1.5">
        {writingTips.map((_, index) => (
          <div
            key={index}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentTipIndex
                ? 'w-8 bg-violet-600'
                : 'w-1.5 bg-stone-300'
            }`}
          />
        ))}
      </div>

      {/* Time estimate */}
      <p className="text-xs text-stone-500 mt-4">
        This typically takes 3-4 minutes for a thorough analysis - plenty of time to grab a snack or take a bathroom break! 
      </p>
    </div>
  );
};

export default AnalysisLoader;